/*
  Shared behavior for every horizontal-scroll + dot-pagination carousel in
  this build (currently: 02-ingredient-carousel, 03-packet-carousel,
  10-main-product). Each
  section keeps its own BEM classes for styling; these generic
  .vp-carousel__* classes are added alongside them purely as JS hooks, so
  one script drives every instance instead of duplicating this logic per
  section.

  ACTIVE-DOT TRACKING went through two failed shapes before the current one,
  both of which tried to name a current ITEM and then light the dot with that
  number -- which only works when the dots and the items are the same set AND
  every item can actually reach the left edge. Neither holds here:

  1. "item whose leading edge is nearest the track's left edge", no end
     handling. Broke on desktop, where a track often can't scroll far enough
     to bring later items to the left edge at all: at 1440 the ingredient
     track has 325px of travel against item offsets of [80,460,840,1220], so
     item 1 stayed "nearest" everywhere and dots 3-4 could never light (the
     packet track, 95px of travel, never got past dot 1).
  2. The same, plus explicit start/end checks and a `min(dots, items)` clamp.
     That fixed the ingredient/packet tracks but read wrong at the end of any
     carousel whose dot count exceeds its item count -- 10-main-product's
     reviews are drawn with 6 dots over 3 screenshot groups, so scrolling
     fully right lit dot 3 of 6 and dots 4-6 were unreachable.

  The dots are an indicator for the TRACK's position, not a per-item index,
  so that is what they now measure: scroll progress mapped across however many
  dots exist (see currentIndex). Item geometry is still what the ARROWS step
  through, since those really do move item to item.

  Item positions are read with getBoundingClientRect rather than offsetLeft:
  offsetLeft is measured from the nearest positioned ancestor, so it only
  agrees with the track's scroll origin by luck.

  SCOPING. A track's dots used to be looked up on its whole <section>, which
  is fine while a section holds one carousel. 10-main-product has two (the
  mobile media gallery and the review screenshots), so both tracks would have
  found the *first* dot list in the section and driven each other. Wrapping
  each carousel in a [data-carousel] element scopes the lookup; sections with
  a single carousel need no wrapper and still fall back to the section.

  NO TRAVEL = NO CONTROLS. When the whole set already fits (scrollWidth <=
  clientWidth) the arrows and the dot row are hidden outright rather than left
  sitting there inert -- vp-pdp-packets hits this at >=1440 with four cards,
  where 4x316 + 3x16 exactly fills the panel, and two faded arrows over a
  single lit dot read as broken rather than as "nothing to scroll". This is
  re-evaluated on every sync, so a narrower viewport or an extra card brings
  them straight back.

  Hiding is BOTH the `hidden` attribute and an inline `display: none`. The
  attribute is the semantic state, but it cannot do the work on its own: every
  section styles its own arrows and dot row with `display: flex` at
  (1,1,0) specificity, which outranks the UA sheet's `[hidden]` rule, so the
  attribute alone would set the state and change nothing on screen. An inline
  style is the one thing that beats a section rule without `!important`, and it
  keeps sections from having to remember a `[hidden]` rule they would only find
  out about by seeing this fail.

  ARROWS are optional: add .vp-carousel__prev / .vp-carousel__next inside
  the same scope and they step one item at a time. Each also carries
  aria-disabled at the end of its direction of travel, so a section can fade an
  arrow that has nowhere left to go. aria-disabled rather than the `disabled`
  property on purpose: `disabled` removes the button from the tab order, which
  would throw keyboard focus back to the top of the page at the moment the user
  reaches the end of the carousel. The click handler is already a no-op there --
  step() finds no target -- so the attribute is presentation, not enforcement.
*/
(function () {
  function initCarousel(track) {
    // Shopify re-renders a section's markup on every theme-editor edit, so this
    // has to be safe to call repeatedly against the same element.
    if (!track || track.dataset.vpCarouselInit === "true") return;
    track.dataset.vpCarouselInit = "true";

    var root = track.closest("[data-carousel]") || track.closest("section") || track.parentElement;
    var items = Array.prototype.slice.call(track.querySelectorAll(".vp-carousel__item"));
    var dotsWrap = root.querySelector(".vp-carousel__dots");
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll(".vp-carousel__dot")) : [];
    if (!items.length || dots.length < 2) return;

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function scrollPad() {
      return parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    }

    /*
      ACTIVE DOT = scroll PROGRESS mapped across the dots, not "which item is
      nearest the left edge". The dots are a position indicator for the track
      as a whole, so the first dot belongs to scrollLeft 0 and the last to
      scrollLeft === maxScroll, whatever the item count is; everything between
      interpolates. Both ends therefore come out exact by construction and need
      no special-casing.

      This replaces a per-item match plus a `lastDot = min(dots, items) - 1`
      clamp, which is what made the end read wrong here: this carousel is drawn
      with 6 dots over 3 placeholder screenshot groups, so scrolling fully
      right lit dot 3 of 6 and dots 4-6 could never light at all. Progress
      mapping lights dot 6 at the end and doesn't care that the dot and item
      counts disagree. Where they DO agree and one item fills the track (every
      other section, and all of them on mobile), item i's resting position is
      i/(n-1) of the travel, so this returns exactly i -- same as before.
    */
    function currentIndex() {
      var max = maxScroll();
      if (max <= 0) return 0; // whole set already on screen
      var progress = Math.min(1, Math.max(0, track.scrollLeft / max));
      return Math.round(progress * (dots.length - 1));
    }

    function syncArrows() {
      if (!prev && !next) return;
      var max = maxScroll();
      // 1px of slack: fractional scrollLeft values mean a track parked at
      // either end rarely reports exactly 0 or exactly maxScroll.
      if (prev) prev.setAttribute("aria-disabled", String(max <= 0 || track.scrollLeft <= 1));
      if (next) next.setAttribute("aria-disabled", String(max <= 0 || track.scrollLeft >= max - 1));
    }

    /*
      See the header note: the attribute carries the meaning, the inline style
      is what actually hides it past the section's own `display: flex`.
    */
    function setShown(el, shown) {
      if (!el) return;
      if (shown) {
        el.removeAttribute("hidden");
        el.style.removeProperty("display");
      } else {
        el.setAttribute("hidden", "");
        el.style.display = "none";
      }
    }

    // Controls exist to move a track that can move. 1px of slack for the same
    // fractional-pixel reason syncArrows uses it.
    function syncVisibility() {
      var canScroll = maxScroll() > 1;
      setShown(prev, canScroll);
      setShown(next, canScroll);
      setShown(dotsWrap, canScroll);
    }

    function sync() {
      syncVisibility();
      syncArrows();
      var index = currentIndex();
      dots.forEach(function (dot, i) {
        var on = i === index;
        dot.classList.toggle("is-active", on);
        if (on) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    }

    // The scrollLeft an item would sit at if it were flush with the track's
    // left edge -- CLAMPED to what the track can actually reach. On a short
    // track several items share the same clamped target (they all pile up at
    // maxScroll), which is exactly what the stepping below has to account for.
    function itemTarget(item) {
      var raw = track.scrollLeft + item.getBoundingClientRect().left - track.getBoundingClientRect().left - scrollPad();
      return Math.max(0, Math.min(maxScroll(), raw));
    }

    /*
      ARROWS step to the nearest REACHABLE item position in `dir`. They used to
      scroll to "the item at index +/- 1", which left the prev arrow dead on
      any track with less travel than one item's offset -- 10-main-product's
      review carousel is the case: 792px of items in a 640px track is 152px of
      travel against item offsets of [0, 250, 558]. One click of next pins it
      to the end, where the index read as the LAST item; prev then aimed at
      item 1, whose target (250) clamps straight back to 152 -- the position it
      was already at, so nothing moved and the carousel could never go back.
      Comparing clamped targets against the live scrollLeft can't get stuck:
      from the end the only target below it is 0, so prev returns to the start.
    */
    function step(dir) {
      var current = track.scrollLeft;
      var best = null;
      items.forEach(function (item) {
        var t = itemTarget(item);
        if (dir < 0 ? t >= current - 1 : t <= current + 1) return;
        if (best === null || (dir < 0 ? t > best : t < best)) best = t;
      });
      if (best === null) return;
      track.scrollTo({ left: best, behavior: "smooth" });
    }

    // Clicking a dot is the inverse of currentIndex(), so click-dot-i always
    // lights dot i. Going via items instead would break wherever there are
    // more dots than items -- dots 4-6 here would all clamp onto the last item
    // and land in the same place. Scroll snapping still gets the final say on
    // where the track comes to rest, which is why sync() runs off `scroll`
    // rather than being set optimistically on click.
    dots.forEach(function (dot, i) {
      dot.addEventListener("click", function () {
        var max = maxScroll();
        if (max <= 0) return;
        track.scrollTo({ left: (i / (dots.length - 1)) * max, behavior: "smooth" });
      });
    });

    // optional prev/next arrows -- step one item from wherever we are now
    var prev = root.querySelector(".vp-carousel__prev");
    var next = root.querySelector(".vp-carousel__next");
    if (prev) prev.addEventListener("click", function () {
      step(-1);
    });
    if (next) next.addEventListener("click", function () {
      step(1);
    });

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        sync();
        ticking = false;
      });
    }

    track.addEventListener("scroll", onScroll);
    // the breakpoint changes card widths and therefore how far the track can
    // scroll, so the active dot has to be recomputed on resize too
    window.addEventListener("resize", onScroll);
    /*
      A window resize is not the only thing that changes the travel: a sticky
      column settling, a late webfont or an image arriving all reflow the track
      without the window changing size at all. That matters more now that the
      controls appear and disappear on the same measurement -- a stale read
      leaves them hidden on a track that can in fact scroll.
    */
    if (typeof ResizeObserver === "function") {
      new ResizeObserver(onScroll).observe(track);
    }
    window.addEventListener("load", onScroll);
    sync();
  }

  function initAll(root) {
    Array.prototype.slice
      .call((root || document).querySelectorAll(".vp-carousel__track"))
      .forEach(initCarousel);
  }

  initAll(document);

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
